// components/CanvasEditor.js - Canvas for drawing masks
import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { FaPencilAlt, FaEraser, FaSave, FaTrash, FaFill, FaUndo, FaRedo } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

function CanvasEditor({
  image,
  brushSize = 20,
  setBrushSize,
  eraseMode = false,
  setEraseMode,
  onSaveMask
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [originalImageData, setOriginalImageData] = useState(null);
  
  // Initialize canvas
  useEffect(() => {
    if (!image || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas dimensions based on image and container
    const containerWidth = container.clientWidth;
    const imgAspectRatio = image.width / image.height;
    
    canvas.width = containerWidth;
    canvas.height = containerWidth / imgAspectRatio;
    
    // Draw the original image
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    
    // Store the original image data for comparison later
    const imgData = context.getImageData(0, 0, canvas.width, canvas.height);
    setOriginalImageData(imgData);
    
    setCtx(context);
    
    // Save initial state to history
    const initialState = context.getImageData(0, 0, canvas.width, canvas.height);
    setHistory([initialState]);
    setHistoryIndex(0);
  }, [image]);
  
  // Save current state to history
  const saveToHistory = () => {
    if (!ctx || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const currentState = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Remove any states after current index (if we've undone and then drawn something new)
    const newHistory = history.slice(0, historyIndex + 1);
    
    // Add current state and update index
    setHistory([...newHistory, currentState]);
    setHistoryIndex(newHistory.length);
  };
  
  // Undo function
  const undo = () => {
    if (historyIndex <= 0 || !ctx || !canvasRef.current) return;
    
    const newIndex = historyIndex - 1;
    const canvas = canvasRef.current;
    
    ctx.putImageData(history[newIndex], 0, 0);
    setHistoryIndex(newIndex);
  };
  
  // Redo function
  const redo = () => {
    if (historyIndex >= history.length - 1 || !ctx || !canvasRef.current) return;
    
    const newIndex = historyIndex + 1;
    const canvas = canvasRef.current;
    
    ctx.putImageData(history[newIndex], 0, 0);
    setHistoryIndex(newIndex);
  };
  
  // Drawing functions
  const startDrawing = (e) => {
    if (!ctx) return;
    setIsDrawing(true);
    
    const { offsetX, offsetY } = getCoordinates(e);
    ctx.beginPath();
    ctx.arc(offsetX, offsetY, brushSize, 0, Math.PI * 2);
    
    if (eraseMode) {
      // Erase by drawing from the original image
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    } else {
      // Draw white overlay (mask area)
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    }
    
    ctx.fill();
  };
  
  const draw = (e) => {
    if (!isDrawing || !ctx) return;
    
    const { offsetX, offsetY } = getCoordinates(e);
    ctx.beginPath();
    ctx.arc(offsetX, offsetY, brushSize, 0, Math.PI * 2);
    ctx.fill();
  };
  
  const stopDrawing = () => {
    if (isDrawing) {
      saveToHistory();
    }
    setIsDrawing(false);
  };
  
  // Helper to get coordinates for both mouse and touch events
  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (event.touches && event.touches[0]) {
      return {
        offsetX: event.touches[0].clientX - rect.left,
        offsetY: event.touches[0].clientY - rect.top
      };
    }
    
    return {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top
    };
  };
  
  // Handle clearing the canvas
  const clearCanvas = () => {
    if (!ctx || !image || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    
    saveToHistory();
  };
  
  // Handle filling the entire canvas
  const fillCanvas = () => {
    if (!ctx || !image || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    // Redraw the original image
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    
    // Fill with semi-transparent white
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    saveToHistory();
  };
  
  // Handle saving the mask
  const saveMask = () => {
    if (!canvasRef.current || !originalImageData) return;
    
    const canvas = canvasRef.current;
    // Create a new canvas for the pure black and white mask
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvas.width;
    maskCanvas.height = canvas.height;
    const maskCtx = maskCanvas.getContext('2d');
    
    // Draw black background (areas to preserve)
    maskCtx.fillStyle = 'black';
    maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
    
    // Get image data from the current canvas (with the overlay)
    const currentImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const currentData = currentImageData.data;
    
    // Get original image data
    const originalData = originalImageData.data;
    
    // Create a new ImageData for the pure mask
    const maskImageData = maskCtx.createImageData(canvas.width, canvas.height);
    const maskData = maskImageData.data;
    
    // Compare original and current images to detect only the drawn mask
    for (let i = 0; i < currentData.length; i += 4) {
      // Check if the current pixel is different from the original
      // Specifically, we're looking for areas that are brighter than the original
      // which would indicate our white overlay
      const originalBrightness = (originalData[i] + originalData[i + 1] + originalData[i + 2]) / 3;
      const currentBrightness = (currentData[i] + currentData[i + 1] + currentData[i + 2]) / 3;
      
      // If current is brighter than original, it's part of our mask
      if (currentBrightness > originalBrightness + 10) {  // Lower threshold to detect any drawn area
        // White pixel in mask (area to inpaint)
        maskData[i] = 255;     // R
        maskData[i + 1] = 255; // G
        maskData[i + 2] = 255; // B
        maskData[i + 3] = 255; // A
      } else {
        // Black pixel in mask (area to preserve)
        maskData[i] = 0;       // R
        maskData[i + 1] = 0;   // G
        maskData[i + 2] = 0;   // B
        maskData[i + 3] = 255; // A
      }
    }
    
    // Put the mask data onto the mask canvas
    maskCtx.putImageData(maskImageData, 0, 0);
    
    // Convert the mask canvas to a data URL
    const maskDataUrl = maskCanvas.toDataURL('image/png');
    
    // Call the callback with the mask data
    if (onSaveMask) {
      onSaveMask(maskDataUrl);
    }
  };
  
  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex bg-background/80 backdrop-blur-sm p-1 rounded-lg shadow-sm">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-3 py-2 rounded-md flex items-center gap-2 transition-colors ${!eraseMode ? 'bg-primary text-white' : 'text-text-light hover:bg-background-dark'}`}
            onClick={() => setEraseMode(false)}
            title={t('drawStep.draw')}
          >
            <FaPencilAlt />
            <span>{t('drawStep.draw')}</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-3 py-2 rounded-md flex items-center gap-2 transition-colors ${eraseMode ? 'bg-primary text-white' : 'text-text-light hover:bg-background-dark'}`}
            onClick={() => setEraseMode(true)}
            title={t('drawStep.erase')}
          >
            <FaEraser />
            <span>{t('drawStep.erase')}</span>
          </motion.button>
        </div>
        
        <div className="flex bg-background/80 backdrop-blur-sm p-1 rounded-lg shadow-sm">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-3 py-2 rounded-md flex items-center gap-2 text-text-light hover:bg-background-dark"
            onClick={fillCanvas}
            title={t('drawStep.fill')}
          >
            <FaFill />
            <span>{t('drawStep.fill')}</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-3 py-2 rounded-md flex items-center gap-2 text-text-light hover:bg-background-dark"
            onClick={clearCanvas}
            title={t('drawStep.clear')}
          >
            <FaTrash />
            <span>{t('drawStep.clear')}</span>
          </motion.button>
        </div>
        
        <div className="flex bg-background/80 backdrop-blur-sm p-1 rounded-lg shadow-sm">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-3 py-2 rounded-md flex items-center gap-2 text-text-light hover:bg-background-dark"
            onClick={undo}
            disabled={historyIndex <= 0}
            title={t('drawStep.undo')}
          >
            <FaUndo />
            <span>{t('drawStep.undo')}</span>
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-3 py-2 rounded-md flex items-center gap-2 text-text-light hover:bg-background-dark"
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            title={t('drawStep.redo')}
          >
            <FaRedo />
            <span>{t('drawStep.redo')}</span>
          </motion.button>
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('drawStep.brushSize')}: {brushSize}px
        </label>
        <input
          type="range"
          min="1"
          max="50"
          value={brushSize}
          onChange={(e) => setBrushSize(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-400 rounded-lg appearance-none cursor-pointer dark:bg-gray-800"
        />
      </div>
      
      <div
        ref={containerRef}
        className="relative border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden"
        style={{ touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className="w-full h-auto bg-white"
        />
      </div>
      
      <div className="mt-4">
        <Button
          onClick={saveMask}
          className="w-full"
          variant="primary"
        >
          <FaSave className="mr-2" />
          {t('drawStep.save')}
        </Button>
      </div>
    </div>
  );
}

export default CanvasEditor;
